import { ReactNode, useEffect, useState } from 'react';
import {
  useTenantId,
  useTenantStore,
  useTenantHydrationStatus,
} from '../../stores/tenantStore';

type TenantGateProps = {
  children: ReactNode;
};

const buildTenantUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    throw new Error(
      'VITE_API_URL is not set. The app cannot resolve its tenant.',
    );
  }
  return `${apiUrl}/v1/tenants/by-domain`;
};

const TenantGate = ({ children }: TenantGateProps) => {
  const tenantId = useTenantId();
  const setTenantId = useTenantStore((state) => state.setTenantId);
  const hydration = useTenantHydrationStatus();
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (hydration.state === 'pending') return;

    const envTenantId = import.meta.env.VITE_TENANT_ID;
    if (envTenantId) {
      setTenantId(envTenantId);
      return;
    }

    let cancelled = false;
    setError(null);

    fetch(buildTenantUrl(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to resolve tenant: ${res.status} ${res.statusText}`,
          );
        }
        const body = (await res.json()) as { tenantId?: string };
        if (!body?.tenantId) {
          throw new Error(
            'Tenant lookup returned no tenantId for this domain.',
          );
        }
        if (!cancelled) setTenantId(body.tenantId);
      })
      .catch((caught) => {
        if (!cancelled) {
          setError(
            caught instanceof Error ? caught : new Error(String(caught)),
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hydration.state, attempt, setTenantId]);

  if (hydration.state === 'pending') return null;

  if (error && !tenantId) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black text-white">
        <div className="max-w-md p-6 text-center">
          <h1 className="mb-2 text-xl font-semibold">
            Couldn&apos;t resolve tenant
          </h1>
          <p className="mb-4 text-sm opacity-80">{error.message}</p>
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
            className="rounded border border-white px-4 py-2 text-sm hover:bg-white hover:text-black"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!tenantId) {
    return <div className="fixed inset-0 z-[9999] bg-black" />;
  }

  return <>{children}</>;
};

export default TenantGate;
